/**
 * Admin inbox for /wms contact enquiries.
 * Super admin always; sub-admin requires `wms.contacts_view`.
 * `wms.contacts_manage` unlocks the Mark Read / Archive buttons.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { Mail, Phone, Building2, User, MessageSquare, Clock, Archive, Check, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'read', label: 'Read' },
  { key: 'archived', label: 'Archived' },
];

export default function AdminWmsContacts() {
  const { authHeaders, isSuperAdmin } = useAdminAuth();
  const { can } = useAdminPermissions();
  const canView   = isSuperAdmin || can('wms.contacts_view');
  const canManage = isSuperAdmin || can('wms.contacts_manage');

  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/wms/contacts`, {
        headers: authHeaders,
        params: status ? { status } : {},
      });
      setRows(r.data?.rows || []);
      setUnread(r.data?.unread || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load enquiries.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, canView, status]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      const r = await axios.patch(`${API}/admin/wms/contacts/${id}`, body, { headers: authHeaders });
      // Replace locally with the fresh server copy so UI stays truthful.
      setRows((prev) => prev.map((x) => (x.id === id ? r.data?.contact || x : x)));
      // Re-fetch the unread badge; cheaper than recounting locally.
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not update.');
    }
  };

  if (!canView) {
    return (
      <main className="p-6 max-w-3xl mx-auto">
        <div className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-700" data-testid="wms-contacts-no-access">
          You need the <code className="px-1 bg-neutral-100 rounded">wms.contacts_view</code> permission to view WMS enquiries.
        </div>
      </main>
    );
  }

  return (
    <main className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="admin-wms-contacts-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold">WMS Marketing</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-black tracking-tight">
            Contact enquiries
            {unread > 0 && (
              <span className="ml-3 inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-amber-500 text-neutral-900 text-xs font-bold">{unread}</span>
            )}
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 disabled:opacity-60 text-sm font-semibold px-3 py-2"
          data-testid="wms-contacts-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-neutral-200">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            onClick={() => setStatus(t.key)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition ${status === t.key ? 'border-amber-500 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
            data-testid={`wms-contacts-tab-${t.key || 'all'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <div className="space-y-3">
        {rows.length === 0 && !loading && (
          <div className="rounded-xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500" data-testid="wms-contacts-empty">
            No enquiries in this view.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            className={`rounded-xl border p-5 ${r.status === 'new' ? 'border-amber-300 bg-amber-50/60' : 'border-neutral-200 bg-white'}`}
            data-testid={`wms-contact-row-${r.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-neutral-900"><Building2 className="w-4 h-4 text-amber-600" /> {r.company}</span>
                  <span className="text-neutral-400">·</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-800"><User className="w-4 h-4 text-neutral-500" /> {r.name}</span>
                  {r.status === 'new' && <span className="ml-1 inline-flex rounded-full bg-amber-500 text-neutral-900 text-[10px] font-bold uppercase px-2 py-0.5">New</span>}
                  {r.status === 'archived' && <span className="ml-1 inline-flex rounded-full bg-neutral-200 text-neutral-700 text-[10px] font-bold uppercase px-2 py-0.5">Archived</span>}
                </div>
                <div className="mt-1 flex items-center gap-4 text-xs text-neutral-600">
                  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3.5 h-3.5" /> {r.email}</a>
                  <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3.5 h-3.5" /> {r.phone}</a>
                  <span className="inline-flex items-center gap-1 text-neutral-500"><Clock className="w-3.5 h-3.5" /> {new Date(r.created_at).toLocaleString()}</span>
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  {r.status !== 'read' && (
                    <button
                      type="button"
                      onClick={() => patch(r.id, { status: 'read' })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1.5"
                      data-testid={`wms-contact-mark-read-${r.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Mark read
                    </button>
                  )}
                  {r.status !== 'archived' && (
                    <button
                      type="button"
                      onClick={() => patch(r.id, { status: 'archived' })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-xs font-semibold px-3 py-1.5"
                      data-testid={`wms-contact-archive-${r.id}`}
                    >
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-start gap-2 text-sm text-neutral-800 whitespace-pre-wrap">
              <MessageSquare className="w-4 h-4 text-neutral-400 shrink-0 mt-0.5" />
              <span>{r.message}</span>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
